import {
  PICTOGRAMS,
  pictogramSlugOf,
} from '~/client/src/shared/icons/pictograms'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { SHIPPED_LOCATION_KIND_OPTIONS } from '~/client/src/shared/resources/domain/creatableLocationKinds'
import { homeKindOfIcon } from '~/client/src/shared/resources/domain/locationKind'
import { hierarchyGroupNesting } from '~/client/src/shared/resources/grouping/hierarchyGrouping'
import {
  createHierarchyAccessors,
  type IHierarchyAccessors,
} from '~/client/src/shared/utils/hierarchyAccessors'
import type { HierarchyTreeOption } from '~/client/src/shared/utils/hierarchyTree'

import { kindLabel } from './iconCatalog'

/**
 * The groups an icon list shows: one per location type, nested under its parent type as every
 * list grouped by Type is, each icon under the type it belongs to first. Company logos and Other
 * hold icons of no type.
 */
export const OTHER_GROUP = 'other'
export const COMPANY_LOGOS_GROUP = 'company_logos'

const seededLocationTypes = (): HierarchyTreeOption[] =>
  SHIPPED_LOCATION_KIND_OPTIONS.map(option => ({
    ...option,
    label: kindLabel(option.value),
  }))

/**
 * A project's Type list, completed with the seeded types it lacks (one a migration has not added
 * yet), so every type is named, nested and ordered by one hierarchy. No list gives the seeded one.
 */
export function locationTypesFrom(
  options?: ReadonlyArray<HierarchyTreeOption>,
): IHierarchyAccessors {
  const seeded = seededLocationTypes()
  if (!options?.length) return createHierarchyAccessors(seeded)

  const known = new Set(options.map(option => option.value))
  return createHierarchyAccessors([
    ...options,
    ...seeded.filter(option => !known.has(option.value)),
  ])
}

export const typeLabel = (kind: string, types: IHierarchyAccessors): string =>
  types.getOwnLabel(kind)

// The groups that are not types sit outside the rule, last.
const PINNED_RANK: Readonly<Record<string, number>> = {
  [COMPANY_LOGOS_GROUP]: 1,
  [OTHER_GROUP]: 2,
}

export function iconGroupOf(iconName: string): string {
  const kind = homeKindOfIcon(iconName)
  if (kind) return kind

  // A brand's glyph is named after its sign's slug, so both land in Company logos.
  const slug = pictogramSlugOf(iconName) ?? iconName
  return PICTOGRAMS.find(p => p.slug === slug)?.category === 'brands'
    ? COMPANY_LOGOS_GROUP
    : OTHER_GROUP
}

/**
 * The product's rule for groups by a hierarchy field, nested: each type under its parent, siblings
 * by name. The groups that are not types stay outside it, at the top level.
 */
export function iconGroupNesting(types: IHierarchyAccessors): {
  getGroupAncestors: (group: string) => string[]
  sortGroups: (a: string, b: string) => number
} {
  const nesting = hierarchyGroupNesting(types)
  return {
    getGroupAncestors: group =>
      group in PINNED_RANK ? [] : nesting.getGroupAncestors(group),
    sortGroups: (a, b) =>
      (PINNED_RANK[a] ?? 0) - (PINNED_RANK[b] ?? 0) ||
      (a in PINNED_RANK ? 0 : nesting.sortGroups(a, b)),
  }
}

export function iconGroupLabel(
  group: string,
  types: IHierarchyAccessors,
): string {
  const t = Localization.translator
  switch (group) {
    case OTHER_GROUP:
      return t.other
    case COMPANY_LOGOS_GROUP:
      return t.iconPicker.companyLogos
    default:
      return typeLabel(group, types)
  }
}
